import React from 'react';
import { IDesignation } from '@/interfaces/designation.mode';
import { IFormData } from '@/interfaces/form.model';
import PrimaryInput from '@/components/ui/PrimaryInput';
import Button from '@/components/ui/Button';

interface IFormMetadataConfigProps {
  formData: IFormData;
  designationOptions: IDesignation[];
  onChange: <K extends keyof IFormData>(field: K, value: IFormData[K]) => void;
  onNext: () => void;
  isViewMode?: boolean;
}

const FormMetadataConfig: React.FC<IFormMetadataConfigProps> = ({
  formData,
  designationOptions,
  onChange,
  onNext,
  isViewMode,
}) => {
  const selectedDesignationIds = Array?.isArray(formData.designationIds)
    ? formData.designationIds
    : [];

  const selectedDesignations = designationOptions.filter((designation) =>
    selectedDesignationIds.includes(designation.id),
  );

  const handleAddDesignation = (designationId: string) => {
    if (!designationId || selectedDesignationIds.includes(designationId)) {
      return;
    }

    onChange('designationIds', [...selectedDesignationIds, designationId]);
  };

  const handleRemoveDesignation = (designationId: string) => {
    onChange(
      'designationIds',
      selectedDesignationIds.filter((id) => id !== designationId),
    );
  };

  return (
    <div className="card shadow-sm border-0 mb-4">
      <div className="card-body p-4">
        <h5 className="fs-4 mb-4">Step 1: Form Metadata</h5>
        <div className="row g-3">
          <div className="col-12">
            <PrimaryInput
              label="Form Name"
              placeholder="Enter form name"
              value={formData.name || ''}
              onChange={(e) => onChange('name', e.target.value)}
              required
              disabled={isViewMode}
            />
          </div>

          <div className="col-12">
            <label className="form-label">
              Description <span className="text-secondary">(Optional)</span>
            </label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Enter form description"
              value={formData.description || ''}
              onChange={(e) => onChange('description', e.target.value)}
              disabled={isViewMode}
            />
          </div>

          <div className="col-12">
            <label className="form-label">Designation</label>
            <div className="border rounded-3 p-3 bg-light">
              <div className="d-flex flex-wrap gap-2 mb-3">
                {selectedDesignations?.length > 0 ? (
                  selectedDesignations?.map((designation) => (
                    <span
                      key={designation.id}
                      className="badge rounded-pill bg-primary-subtle text-primary border border-primary-subtle d-inline-flex align-items-center gap-2 px-3 py-2"
                    >
                      <span>{designation?.name}</span>
                      {!isViewMode && (
                        <button
                          type="button"
                          className="btn btn-sm btn-light rounded-circle border-0 p-0 d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{
                            width: '20px',
                            height: '20px',
                            lineHeight: 1,
                          }}
                          aria-label={`Remove ${designation?.name}`}
                          onClick={() =>
                            handleRemoveDesignation(designation?.id)
                          }
                        >
                          <span aria-hidden="true" style={{ fontSize: '12px' }}>
                            {/* X */}
                            <i className="bi bi-x"></i>
                          </span>
                        </button>
                      )}
                    </span>
                  ))
                ) : (
                  <span className="text-muted small">
                    No designation selected yet.
                  </span>
                )}
              </div>

              <select
                className="form-select"
                value=""
                onChange={(e) => handleAddDesignation(e.target.value)}
                disabled={isViewMode}
              >
                <option value="">Select designation</option>
                {designationOptions?.map((designation) => {
                  const isSelected = selectedDesignationIds?.includes(
                    designation?.id,
                  );
                  return (
                    <option
                      key={designation.id}
                      value={designation.id}
                      disabled={isSelected}
                    >
                      {designation.name}
                      {isSelected ? ' (selected)' : ''}
                    </option>
                  );
                })}
              </select>
              <div className="form-text">
                Selected designations stay disabled in the dropdown until you
                remove them.
              </div>
            </div>
          </div>

          <div className="col-12">
            <label className="form-label">Status</label>
            <select
              className="form-select"
              value={String(formData.status || 'draft')}
              onChange={(e) => onChange('status', e.target.value)}
              disabled={isViewMode}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              {/* <option value="archived">Archived</option> */}
            </select>
          </div>

          <div className="col-12 d-flex justify-content-end mt-4">
            <Button
              className="btn btn-primary px-4"
              type="button"
              onClick={onNext}
              disabled={!formData.name?.trim()}
            >
              {isViewMode ? 'View Sections' : 'Next: Manage Sections'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormMetadataConfig;
